import { z } from 'zod'

export const MAX_SUPPORT_DOCUMENT_BYTES = 5 * 1024 * 1024

// reserved upload-key prefix for claims of organizations not in our DB yet
export const NEW_ORG_UPLOAD_PREFIX = 'new-org'

export const SUPPORT_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

const LINKEDIN_OR_X_DOMAINS = ['linkedin.com', 'x.com', 'twitter.com']

export function isLinkedInOrXUrl(value: string): boolean {
  try {
    const hostname = new URL(value).hostname.toLowerCase()
    return LINKEDIN_OR_X_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))
  } catch {
    return false
  }
}

export const SupportDocumentSchema = z
  .object({
    key: z.string().min(1),
    fileName: z.string().min(1),
    mimeType: z.enum(SUPPORT_DOCUMENT_MIME_TYPES),
    sizeBytes: z.number().int().positive().max(MAX_SUPPORT_DOCUMENT_BYTES),
    signature: z.string().min(1).optional(),
  })
  .strict()

export const ClaimRequestBodySchema = z
  .object({
    marketMakerId: z.string().trim().min(1).max(200).optional(),
    organizationName: z.string().trim().min(1).max(200),
    position: z.string().trim().min(1).max(200),
    hasForgdContact: z.boolean(),
    forgdContactName: z.string().trim().min(1).max(200).optional(),
    fullName: z.string().trim().min(1).max(200),
    socialProfileUrl: z.string().url().max(500).refine(isLinkedInOrXUrl, 'Must be a LinkedIn or X profile URL'),
    organizationEmail: z.string().email().max(320),
    telegram: z.string().trim().max(100).optional(),
    supportDocument: SupportDocumentSchema.optional(),
    alignmentConsent: z.literal(true),
    termsConsent: z.literal(true),
    turnstileToken: z.string().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.hasForgdContact && !value.forgdContactName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['forgdContactName'],
        message: 'Forgd team member name is required',
      })
    }
  })

export type ClaimRequestBodyType = z.infer<typeof ClaimRequestBodySchema>
export type SupportDocumentType = z.infer<typeof SupportDocumentSchema>
